import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import ServicesSection from "./components/ServicesSection";
import ProcessSection from "./components/ProcessSection";
import AboutSection from "./components/AboutSection";
import CTASection from "./components/CTASection";
import BookingSection from "./components/BookingSection";
import ContactSection from "./components/ContactSection";
import ROICalculator from "./components/ROICalculator";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": SITE_URL,
      "name": "GK Vertex Consulting",
      "description": "Premium custom web design and digital marketing agency that builds high-converting websites.",
      "inLanguage": "en-US",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/?s={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "ProfessionalService",
      "@id": `${SITE_URL}/#organization`,
      "name": "GK Vertex Consulting",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/favicon.ico`,
        "width": 512,
        "height": 512
      },
      "email": "gkvertexconsulting@gmail.com",
      "description": "GK Vertex Consulting builds custom, high-converting websites and digital experiences. Expert web design, SEO, and digital marketing that drives real leads and measurable growth.",
      "priceRange": "$$",
      "areaServed": "Worldwide",
      "serviceType": ["Web Design", "Digital Marketing", "SEO", "Lead Generation"],
      "knowsAbout": ["Web Design", "SEO Optimization", "Digital Marketing", "Lead Generation", "Custom Website Development"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Web Design & Digital Marketing Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Custom Web Design",
              "description": "100% custom-built websites designed to convert visitors into paying clients."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "SEO Optimization",
              "description": "Search engine optimization strategies that drive organic traffic and improve rankings."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Digital Marketing",
              "description": "Comprehensive digital marketing campaigns that generate real leads and measurable growth."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Lead Generation",
              "description": "Targeted lead generation strategies that connect you with your ideal customers."
            }
          }
        ]
      },
      "sameAs": []
    },
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      "url": SITE_URL,
      "name": "GK Vertex Consulting | Custom Web Design & Digital Marketing Agency",
      "description": "GK Vertex Consulting builds custom, high-converting websites and digital experiences. Expert web design, SEO, and digital marketing that drives real leads and measurable growth for your business.",
      "inLanguage": "en-US",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#organization` },
      "dateModified": "2026-04-05",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL }
        ]
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How long does it take to build a website?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our average delivery time is 10 days from kickoff to launch. We move fast without cutting corners."
          }
        },
        {
          "@type": "Question",
          "name": "Do you use templates?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Never. Every website we build is 100% custom-crafted to reflect your brand and business goals."
          }
        },
        {
          "@type": "Question",
          "name": "What results can I expect?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our clients see an average of 3x lead growth after launching their new website with GK Vertex Consulting."
          }
        },
        {
          "@type": "Question",
          "name": "What services does GK Vertex Consulting offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer custom web design, SEO optimization, digital marketing, and lead generation services tailored to grow your business online."
          }
        },
        {
          "@type": "Question",
          "name": "How much does a custom website cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our pricing depends on the scope and complexity of your project. Contact us for a free consultation and custom quote tailored to your business needs."
          }
        }
      ]
    }
  ]
};

export default function HomePage() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "home-jsonld";
    script.text = JSON.stringify(homeJsonLd);
    document.head.appendChild(script);
    document.title = "GK Vertex Consulting | Custom Web Design & Digital Marketing Agency";
    return () => {
      const el = document.getElementById("home-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]">
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <ProcessSection />
        <AboutSection />
        <CTASection />
        <ROICalculator />
        <BookingSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
